/**
 * S3 Service - Direct S3 Access using Cognito Identity Pool
 * 
 * This service uses AWS Amplify Storage to fetch photos and videos directly from S3
 * using temporary STS credentials from Cognito Identity Pool.
 * 
 * Security: Access is scoped to the user's facility via ABAC (Attribute-Based Access Control)
 */


import { getUrl } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import cacheService from './cacheService';

class S3Service {
  /**
   * Validate S3 key path format for PHOTOS bucket
   * IAM Policy requires: FAC-XXXX/entity_type/entity_id.ext
   * 
   * STRICT ABAC ENFORCEMENT: Rejects non-compliant paths
   * 
   * @param {string} s3Key - S3 key from database
   * @param {string} facilityId - User's facility ID from Cognito
   * @returns {string} - Validated S3 key
   * @throws {Error} - If path doesn't match ABAC requirements
   */
    validatePhotoS3KeyPath(s3Key, facilityId) {
    if (!s3Key || !facilityId) {
      throw new Error('S3 key and facility ID are required');
    }

    // Handle both formats:
    // 1. FAC-0001/residents/RES-xxx.jpg (correct format)
    // 2. residents/FAC-0001/RES-xxx/avatar.jpg (legacy format from database)
    
    const validEntityTypes = ['residents', 'caregivers', 'admins', 'devices'];
    const parts = s3Key.split('/');
    
    // Check if it's the correct format: FAC-XXXX/entity_type/...
    if (s3Key.startsWith(`${facilityId}/`)) {
      // Correct format: FAC-0001/residents/...
      if (parts.length < 3) {
        throw new Error('Invalid S3 path: Must be in format FAC-XXXX/entity_type/entity_id');
      }
      
      const entityType = parts[1];
      if (!validEntityTypes.includes(entityType)) {
        throw new Error(`Invalid entity type: ${entityType}. Must be one of: ${validEntityTypes.join(', ')}`);
      }
      
      console.log(`[S3Service] ABAC validation passed: ${s3Key}`);
      return s3Key;
    }
    
    // Check if it's the legacy format: residents/FAC-XXXX/...
    const entityType = parts[0];
    if (validEntityTypes.includes(entityType) && parts.length >= 3 && parts[1] === facilityId) {
      // Legacy format detected - need to reformat to FAC-XXXX/residents/...
      console.warn(`[S3Service] Legacy S3 key format detected: ${s3Key}`);
      console.warn(`[S3Service] Converting to correct format...`);
      
      // Reformat: residents/FAC-0001/RES-xxx/avatar.jpg -> FAC-0001/residents/RES-xxx.jpg
      const newKey = `${facilityId}/${entityType}/${parts.slice(2).join('/')}`;
      console.log(`[S3Service] Converted key: ${newKey}`);
      return newKey;
    }
    
    // Neither format matches - reject
    console.error(`[S3Service] ABAC VIOLATION: S3 key does not match expected formats`);
    console.error(`[S3Service] Expected: ${facilityId}/entity_type/... OR entity_type/${facilityId}/...`);
    console.error(`[S3Service] Got: ${s3Key}`);
    throw new Error(`Access denied: Invalid S3 path format. Path must contain ${facilityId}`);
  }

  /**
   * Validate S3 key path format for VIDEOS bucket
   * Videos are in a separate bucket with structure: {facility_id}/{resident_id}/{fall_id}.mp4
   * Format: FAC-XXXX/RES-XXXX/FALL-XXXX.mp4
   * 
   * @param {string} s3Key - S3 key from database
   * @param {string} facilityId - User's facility ID from Cognito
   * @returns {string} - Validated S3 key
   * @throws {Error} - If path doesn't match requirements
   */
  validateVideoS3KeyPath(s3Key, facilityId) {
    if (!s3Key || !facilityId) {
      throw new Error('S3 key and facility ID are required');
    }

    // Videos must start with facility ID
    if (!s3Key.startsWith(`${facilityId}/`)) {
      console.error(`[S3Service] VIDEO ACCESS DENIED: S3 key does not start with facility ID`);
      console.error(`[S3Service] Expected: ${facilityId}/...`);
      console.error(`[S3Service] Got: ${s3Key}`);
      throw new Error(`Access denied: Video path must start with ${facilityId}/`);
    }

    // Validate structure: FAC-XXXX/RES-XXXX/FALL-XXXX.mp4
    const parts = s3Key.split('/');
    if (parts.length !== 3) {
      console.error(`[S3Service] Invalid video path structure`);
      console.error(`[S3Service] Expected: ${facilityId}/RES-XXXX/FALL-XXXX.mp4`);
      console.error(`[S3Service] Got: ${s3Key}`);
      throw new Error(`Invalid video path: Must be in format ${facilityId}/resident_id/fall_id.mp4`);
    }

    const residentId = parts[1];
    const fallFile = parts[2];

    // Validate resident ID format
    if (!residentId.startsWith('RES-')) {
      throw new Error(`Invalid resident ID in video path: ${residentId}. Must start with RES-`);
    }

    // Validate fall file format (supports both FALL-XXXX.mp4 and UUID.mp4 formats)
    if (!fallFile.endsWith('.mp4')) {
      throw new Error(`Invalid fall file in video path: ${fallFile}. Must be a .mp4 file`);
    }
    
    // Accept either FALL-XXXX format or UUID format
    const isFallFormat = fallFile.startsWith('FALL-');
    const isUuidFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.mp4$/i.test(fallFile);
    
    if (!isFallFormat && !isUuidFormat) {
      throw new Error(`Invalid fall file in video path: ${fallFile}. Must be either FALL-XXXX.mp4 or UUID.mp4 format`);
    }

    console.log(`[S3Service] Video path validation passed: ${s3Key}`);
    return s3Key;
  }

  /**
   * Get user's facility ID from Cognito session
   * @returns {Promise<string|null>} - Facility ID or null
   */
  async getUserFacilityId() {
    try {
      const session = await fetchAuthSession();
      const facilityId = session?.tokens?.idToken?.payload?.['custom:facility_id'];
      
      if (facilityId) {
        console.log('[S3Service] Facility ID:', facilityId);
      } else {
        console.warn('[S3Service] No facility ID found in session');
      }
      
      return facilityId || null;
    } catch (error) {
      console.error('[S3Service] Failed to get facility ID:', error);
      return null;
    }
  }

  /**
   * Get a temporary URL for a photo from S3 - WITH CACHING
   * @param {string} s3Key - The S3 key (e.g., "FAC-0001/residents/RES-123.jpg")
   * @returns {Promise<string|null>} - Temporary URL or null if not found
   */
  async getPhotoUrl(s3Key) {
    if (!s3Key || s3Key.trim() === '') {
      console.log('[S3Service] No S3 key provided for photo');
      return null;
    }

    // Check cache first
    const cacheKey = cacheService.generateKey('photo', s3Key);
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Get user's facility ID and validate ABAC compliance
      const facilityId = await this.getUserFacilityId();
      if (!facilityId) {
        throw new Error('User facility ID not found in session');
      }
      
      // STRICT ABAC: Validate path format for PHOTOS bucket
      const validatedKey = this.validatePhotoS3KeyPath(s3Key, facilityId);
      console.log('[S3Service] ABAC-validated photo S3 key:', validatedKey);
      
      // Use Amplify's getUrl - it will use the bucket from aws-config.js
      const result = await getUrl({
        path: validatedKey,
        options: {
          expiresIn: 3600, // 1 hour
        }
      });
      
      const url = result.url.toString();
      console.log('[S3Service] Photo URL generated successfully');
      
      // Cache the URL (expires before S3 URL expires)
      cacheService.set(cacheKey, url, cacheService.defaultTTL.photos);
      
      return url;
    } catch (error) {
      console.error('[S3Service] Failed to get photo URL:', error);
      console.error('[S3Service] S3 Key:', s3Key);
      return null;
    }
  }

  /**
   * Get a temporary URL for a video from S3 - WITH CACHING
   * @param {string} s3Key - The S3 key for video
   * @returns {Promise<string|null>} - Temporary URL or null if not found
   */
  async getVideoUrl(s3Key) {
    if (!s3Key || s3Key.trim() === '') {
      console.log('[S3Service] No S3 key provided for video');
      return null;
    }

    // Check cache first
    const cacheKey = cacheService.generateKey('video', s3Key);
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Get user's facility ID and validate ABAC compliance
      const facilityId = await this.getUserFacilityId();
      if (!facilityId) {
        throw new Error('User facility ID not found in session');
      }
      
      // Validate path format for VIDEOS bucket (simpler than photos)
      const validatedKey = this.validateVideoS3KeyPath(s3Key, facilityId);
      console.log('[S3Service] Validated video S3 key:', validatedKey);
      
      // For videos, we need to use a different bucket
      // Use AWS SDK directly since Amplify only supports one bucket
      const { S3Client, GetObjectCommand } = await import('@aws-sdk/client-s3');
      const { getSignedUrl } = await import('@aws-sdk/s3-request-presigner');
      const { fetchAuthSession } = await import('aws-amplify/auth');
      
      // Get Cognito credentials
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      if (!credentials) {
        throw new Error('No credentials available');
      }
      
      // Create S3 client with Cognito credentials
      const s3Client = new S3Client({
        region: 'us-east-1',
        credentials: credentials
      });
      
      // Create GetObject command
      const command = new GetObjectCommand({
        Bucket: 'aitcare-fall-clips-encrypted-dev',
        Key: validatedKey,
        ResponseContentType: 'video/mp4'
      });
      
      // Generate presigned URL (5 minutes for security)
      const url = await getSignedUrl(s3Client, command, { expiresIn: 300 });
      
      console.log('[S3Service] Video URL generated successfully (AWS SDK)');
      
      // Cache the URL (expires before S3 URL expires)
      cacheService.set(cacheKey, url, cacheService.defaultTTL.videos);
      
      return url;
    } catch (error) {
      console.error('[S3Service] Failed to get video URL:', error);
      console.error('[S3Service] S3 Key:', s3Key);
      return null;
    }
  }

  /**
   * Upload a photo to S3
   * @param {string} s3Key - The S3 key where to upload
   * @param {File} file - The file to upload
   * @returns {Promise<boolean>} - Success status
   */
  async uploadPhoto(s3Key, file) {
    try {
      console.log('[S3Service] Uploading photo to:', s3Key);
      
      // Note: This will be implemented when frontend handles uploads
      // For now, backend still handles uploads via multipart form
      console.warn('[S3Service] Upload not yet implemented - backend handles uploads');
      return false;
    } catch (error) {
      console.error('[S3Service] Failed to upload photo:', error);
      return false;
    }
  }
}

const s3Service = new S3Service();
export default s3Service;
